// Ported from platform-legacy/readonly/platform/components/FilesSidebarGettingStarted.js
// v3 → v3.14: extend → extends, top-level props: wrapper stripped. The
// childExtends entry that used a v3-style `props: (el, s) => …` factory
// returning a shape object was flattened to per-property reactive
// accessors that each compute `isDone` lazily — same effective behaviour,
// v3.14-compliant.
//
// All el.call('X', …) invocations preserved verbatim (FA001).
//
// TODO: verify FilesSidebarSection / FilesSidebarTitle / FilesSidebarList /
// FilesSidebarListItem / FileSidebarAddNewItem dependencies — these are
// editor-canvas chrome components that may need to come from a separate
// sub-app's components/index.js when this is mounted.

export const FilesSidebarGettingStarted = {
  // FA204 / frank trap — isDone used to be a bare module-scope const read
  // from inside the childExtends `pointerEvents`/`text`/`visibility`/
  // `opacity`/`color` factories; it reads as undefined once this component
  // is frank.toJSON'd for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called
  // via `el.scope.isDone`.
  scope: {
    isDone: (el) => el.call?.('exec', el.parent?.isDone, el.parent)
  },
  extends: 'FilesSidebarSection',
  theme: 'layer',
  padding: '- Y1',
  margin: '- -Y1',
  textTransform: 'none',
  round: '--canvas-round',
  if: (el) => el.isEditMode?.(),
  FilesSidebarTitle: {
    position: 'relative',
    theme: null,
    'IconButton.question': null,
    IconButton_arrow: null,
    Span: {
      fontWeight: '300',
      margin: 'Z - Z Y2',
      Text: {
        text: '{{ editor.dashboard.gettingStarted | polyglot }}'
      }
    }
  },
  FilesSidebarList: {
    padding: '- - Z',
    FileSidebarAddNewItem: null,
    onMouseleave: null,
    hide: false,
    childExtends: [
      {
        // v3 had a single `props: (el, s) => { isDone = …; return {…} }`
        // factory here. Flattened to per-property reactive accessors —
        // each re-evaluates `isDone` against the parent's own `isDone`
        // predicate at render time.
        pointerEvents: (el, s) => (el.scope.isDone(el) ? 'none' : 'auto'),
        Span: {
          text: (el, s) => (el.scope.isDone(el.parent) ? '  ' : '・'),
          Icon: {
            visibility: (el, s) => (el.scope.isDone(el.parent.parent) ? 'visible' : undefined),
            opacity: (el, s) => (el.scope.isDone(el.parent.parent) ? 1 : undefined),
            color: (el, s) => (el.scope.isDone(el.parent.parent) ? 'green' : undefined)
          }
        }
      },
      'FilesSidebarListItem'
    ],
    childProps: {
      fontWeight: '400',
      isActive: false,
      isCurrent: false,
      Span: {
        Icon: {
          name: 'check',
          onClick: (ev, el, s) => {}
        }
      }
    },
    childrenAs: 'props',
    children: () => [
      {
        Text: {
          text: '{{ learn.checklist.createComponent | polyglot }}'
        },
        isDone: (el) => Object.keys(el.getSchema?.('components') || {}).length > 0,
        onClick: async (ev, el) => {
          ev.stopPropagation()
          await el.call('openRouterModal', '/add-component')
        }
      },
      {
        Text: {
          text: '{{ learn.checklist.reuseComponent | polyglot }}'
        },
        isDone: (el) => Object.keys(el.getSchema?.('components') || {}).length > 1,
        onClick: async (ev, el) => {
          ev.stopPropagation()
          const componentFound = Object.values(el.getSchema?.('components') || {})[0]
          if (!componentFound?.key) {
            return el.call('openNotification', {
              title: el.call('polyglot', 'learn.checklist.noComponent.title'),
              message: el.call('polyglot', 'learn.checklist.noComponent.message'),
              type: 'transparentPattern'
            })
          }
          await el.call('openRouterModal', '/add-component', {
            key: componentFound.key + 'Reused',
            title: (componentFound.title || componentFound.key) + ' Reused',
            type: 'components',
            value: {
              [componentFound.key]: {}
            },
            code: `export default {\n  // Reusable component:\n  ${componentFound.key}: {\n    // Overwrite properties... \n  },\n}`,
            advanced: true
          })
        }
      },
      {
        Text: {
          text: '{{ learn.checklist.createPage | polyglot }}'
        },
        isDone: (el) => (el.getSchema?.('pages') || {})['/'],
        onClick: async (ev, el) => {
          ev.stopPropagation()
          await el.call('openRouterModal', '/add-page', {
            title: 'Main page',
            key: '/',
            type: 'pages',
            value: {
              '/': {
                H1: {
                  text: 'Hello Symbols'
                }
              }
            },
            code: `export default {\n  '/': {\n    H1: { text: 'Hello Symbols' } \n  },\n}`,
            advanced: true
          })
        }
      },
      {
        Text: {
          text: '{{ learn.checklist.openPage | polyglot }}'
        },
        isDone: (el) => el.call?.('getCookie', el.getAppKey?.() + '_projectRemotePageWasViewed'),
        onClick: (ev, el) => {
          ev.stopPropagation()
          const pageFound = Object.values(el.getSchema?.('pages') || {})[0]
          if (!pageFound?.key) {
            return el.call('openNotification', {
              title: el.call('polyglot', 'learn.checklist.noPage.title'),
              message: el.call('polyglot', 'learn.checklist.noPage.message'),
              type: 'transparentPattern'
            })
          }
          const url = `https://${el.getAppKey?.()}/${pageFound.key}`
          window.open(url, 'Symbols preview')
          el.call('setCookie', el.getAppKey?.() + '_projectRemotePageWasViewed', true)
        }
      },
      {
        Text: {
          text: '{{ learn.checklist.importLibrary | polyglot }}'
        },
        // add flag to confirm component was added
        // isDone: el =>
        //   Object.keys(el.getSchema('components')).length > 1,
        onClick: (ev, el) => {
          ev.stopPropagation()
          el.call('openLibrary')
        }
      },
      {
        Text: {
          text: '{{ learn.checklist.uploadLogo | polyglot }}'
        },
        isDone: (el) => Object.keys(el.getData?.('files') || {}).length > 0,
        onClick: async (ev, el) => {
          ev.stopPropagation()
          await el.call('openRouterModal', '/add-file')
        }
      },
      {
        Text: {
          text: '{{ learn.checklist.addColor | polyglot }}'
        },
        isDone: (el) => Object.keys(el.getData?.('designSystem')?.COLOR || {}).length > 2,
        onClick: (ev, el) => {
          ev.stopPropagation()
          el.call('activeAddingFloatingPanel', ev, 'color')
        }
      },
      // {
      //   Text: { text: 'Setup a global theme' },
      //   isDone: el => Boolean(el.getData('designSystem').THEME?.document['@' + el.call('getSystemGlobalTheme')]),
      //   onClick: (ev, el) => {
      //     ev.stopPropagation()
      //     el.call('activeAddingFloatingPanel', ev, 'global_theme')
      //   }
      // }
      {
        Text: {
          text: '{{ learn.checklist.publish | polyglot }}'
        },
        // isDone: el => {
        //   const version = el.getRootState('version')
        //   const parts = version?.split('.') || []
        //   return parts.length === 3 && parts[1] !== '0'
        // },
        isDone: (el) => el.call?.('getCookie', el.getAppKey?.() + '_projectWasPublished'),
        onClick: async (ev, el) => {
          ev.stopPropagation()
          await el.call('openRouterModal', '/publish')
        }
      }
    ]
  }
}
